import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { SitesRepository } from '@/repositories/SitesRepository'

// Query keys
export const siteQueryKeys = {
  all: ['sites'] as const,
  site: (userId: string) => ['sites', userId] as const,
}

// Site query hook
export function useSiteQuery(userId: string | undefined) {
  return useQuery({
    queryKey: siteQueryKeys.site(userId || ''),
    queryFn: () => SitesRepository.getByUserId(),
    enabled: !!userId,
    select: (data) => {
      if (data.error) {
        throw new Error(data.error.message)
      }
      return data.data
    },
  })
}

// Site update mutation hook
export function useSiteUpdateMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      siteId,
      userId,
      data,
    }: {
      siteId: string
      userId: string
      data: unknown // Accept any data and let SitesRepository handle validation
    }) => {
      const result = await SitesRepository.update(siteId, userId, data)
      if (result.error) {
        throw new Error(result.error.message)
      }
      return result.data
    },
    onSuccess: (_, { userId }) => {
      // Invalidate the site query to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: siteQueryKeys.site(userId),
      })
    },
  })
}

// Hook to invalidate site cache (useful after publishing)
export function useInvalidateSiteCache() {
  const queryClient = useQueryClient()

  return (userId: string) => {
    queryClient.invalidateQueries({
      queryKey: siteQueryKeys.site(userId),
    })
  }
}